<template>
    <div>
        <!--Drag and drop box-->
        <!--File Preview-->
        <file-drag-and-drop
            :disabled="disabled"
            :show-uploader="showUploader"
            :allowed-extensions="allowedExtensions"
            :dragging.sync="dragging"
            ref="dragAndDrop"
            @change="fileChanged"
        >
            <div
                v-if="!uploading && contentSet && showPreview"
                class="d-flex justify-center"
            >
                <div style="position: relative">
                    <slot name="preview">
                        <img
                            v-if="fileType === 'image'"
                            :style="ivStyles"
                            style="object-fit: contain"
                            :src="contentSrc"
                            :alt="content.name"
                        />

                        <video
                            v-else-if="fileType === 'video'"
                            :style="ivStyles"
                            style="object-fit: contain"
                            :src="contentSrc"
                            autoplay
                            muted
                            loop
                            playsinline
                        ></video>

                        <div
                            v-else
                            class="
                                d-flex
                                flex-column
                                align-center
                                justify-center
                            "
                        >
                            <v-icon large>mdi-file-document</v-icon>
                            <span v-text="content.name"></span>
                        </div>
                    </slot>

                    <!--Edit button-->
                    <v-btn
                        v-if="contentSet && !disabled"
                        fab
                        x-small
                        :disabled="uploading"
                        elevation="0"
                        absolute
                        style="top: -10px; right: -10px"
                    >
                        <v-icon>{{ editIcon }}</v-icon>
                    </v-btn>
                </div>
            </div>
        </file-drag-and-drop>

        <div style="min-width: 200px; max-width: 100%">
            <v-progress-linear
                v-if="progress"
                stream
                buffer-value="0"
                :value="progress"
            ></v-progress-linear>
        </div>

        <v-btn
            v-if="contentSet && !progress && clearButton"
            class="mt-3"
            color="error"
            small
            @click="clear"
        >
            Clear
        </v-btn>
    </div>
</template>

<script>
import FileDragAndDrop from "./FileDragAndDrop";
import Vapor from "../../Modules/Vapor";

export default {
    name: "FileUploader",
    components: { FileDragAndDrop },
    props: {
        /**
         * the v-model
         */
        value: {
            type: Object,
        },

        /**
         * The max image dimension in px
         */
        maxSize: {
            type: Number,
            default: null,
        },

        /**
         * The max file size in MB
         */
        maxFileSize: {
            type: Number,
            default: null,
        },

        /**
         * Allowed file extensions array
         * e.g - ['.jpg', '.jpeg', '.png', '.mp4']
         */
        allowedExtensions: {
            type: Array,
            default: null,
        },

        /**
         * Route to put the content too
         */
        updateRoute: {
            type: String,
            default: null,
        },

        imageStyles: {
            type: Object,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        editIcon: {
            type: String,
            default: "mdi-pencil",
        },

        clearButton: {
            type: Boolean,
            default: true,
        },

        visibilityPrivate: {
            type: Boolean,
            default: false,
        },

        returnFile: {
            type: Boolean,
            default: false,
        },

        showPreview: {
            type: Boolean,
            default: true,
        },

        uploadMethod: {
            type: Function,
            default: null,
        },

        dontReadFileToDataUrl: {
            type: Boolean,
            default: false,
        },

        dontAppendDataUrlToContentPath: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            uploadProgress: 0,
            remoteProgress: 0,
            content: _.clone(this.value),
            type: "",
            name: "",
            dragging: false,
            dataUrl: null,
            extensionTypes: {
                mov: "video",
                mp4: "video",
                png: "image",
                jpg: "image",
                jpeg: "image",
            },
        };
    },
    watch: {
        value: {
            handler(newVal) {
                this.content = _.clone(newVal);
            },
            deep: true,
        },
        uploading() {
            this.hideOther();
        },
    },
    methods: {
        getFileType(extension) {
            let types = ["image", "video"];

            if (types.includes(extension)) {
                return extension;
            }

            return this.extensionTypes[extension] ?? "other";
        },

        /**
         * When a new file is selected, begin the upload process
         */
        async fileChanged(file) {
            if (!this.content) {
                this.content = {};
            }

            if (this.disabled) {
                return;
            }

            if (!file) {
                return;
            }

            if (!(await this.checkExtension(file))) {
                alert("Please ensure the file is a supported type");
                return;
            }

            if (!(await this.checkFileSize(file))) {
                alert("Please ensure the file is of the correct size");
                return;
            }

            this.uploadProgress = 1;

            this.type = this.getExtension(file);
            this.name = file.name;

            this.$set(this.content, "type", this.type);
            this.$set(this.content, "name", this.name);

            this.uploadProgress = 5;

            file = await this.resize(file);

            this.uploadProgress = 25;

            let upload = await this.uploadMethodSelector(
                file,
                this.updateRemoteProgress
            );

            this.$set(this.content, "tmp", upload.key);
            this.$set(this.content, "key", upload.key);

            this.uploadProgress = 49;

            if (this.dontReadFileToDataUrl) {
                this.dataUrl = true;
            } else {
                this.dataUrl = await this.readFile(file);
            }

            if (!this.dontAppendDataUrlToContentPath) {
                this.$set(this.content, "path", this.dataUrl);
            }

            if (this.updateRoute) {
                let response = await this.updateContent(this.content);

                this.content = response.data;
            }

            if (this.returnFile) {
                this.$set(this.content, "rawFile", file);
            }

            this.resetProgress();

            this.finish();
        },

        checkFileSize(file) {
            return (
                !this.maxFileSize || file.size < this.maxFileSize * 1.15 * 1e6
            );
        },

        /**
         * Check that the file type is what we want
         */
        checkExtension(file) {
            if (!this.allowedExtensions) {
                return true;
            }

            return this.allowedExtensions.includes(
                "." + this.getExtension(file)
            );
        },

        /**
         * get the content type based on extention
         * @param file
         */
        getExtension(file) {
            return file.name.split(".").pop().toLowerCase();
        },

        /**
         * Read the file and return the data url
         */
        readFile(file) {
            return new Promise((resolve) => {
                let reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(file);
            });
        },

        /**
         * Resize the image if necessary
         */
        async resize(file) {
            if (this.maxSize && this.getFileType(this.type) === "image") {
                return new File(
                    [await this.reduceImage(file, this.maxSize)],
                    file.name
                );
            } else return file;
        },

        /**
         * Upload the file to S3
         * return the temp upload
         */
        uploadToS3(file, updateProgress) {
            return Vapor.store(file, {
                progress: (progress) => {
                    updateProgress(progress);
                },
                visibility: this.visibilityPrivate ? "private" : "public-read",
                signedStorageUrl: this.route("signed-storage-url.store"),
            });
        },

        updateRemoteProgress(value) {
            this.remoteProgress = Math.round((value * 100) / 2);
        },

        uploadMethodSelector(...args) {
            if (typeof this.uploadMethod === "function") {
                return this.uploadMethod(...args);
            }

            return this.uploadToS3(...args);
        },

        /**
         * Reset the progress Counters
         */
        resetProgress() {
            this.uploadProgress = 0;
            this.remoteProgress = 0;
        },

        /**
         * Emit the content when done
         */
        finish() {
            this.$refs.dragAndDrop?.reset();
            this.$emit("input", this.content);
            this.$emit("newUpload", this.content);
        },

        /**
         * Update the backend
         */
        updateContent(content) {
            let temp = _.cloneDeep(content);
            delete temp.path;
            delete temp.src;
            return this.$http.put(this.updateRoute, {
                tempContent: temp,
            });
        },

        hideOther() {
            let x = this.showUploader || this.uploading;

            this.$emit("hideOther", x);
        },

        clear() {
            this.dataUrl = null;
            this.$emit("clear");
        },
    },

    computed: {
        showUploader() {
            return (this.dragging || !this.contentSet) && !this.uploading;
        },

        fileType() {
            if (!this.contentSet) {
                return null;
            }

            return this.getFileType(this.content.type);
        },
        contentSrc() {
            if (!this.contentSet) {
                return null;
            }

            return this.dataUrl ?? this.content.path ?? this.content.src;
        },
        /**
         * is there content set?
         * @returns {boolean}
         */
        contentSet() {
            if (!this.content) {
                return false;
            }
            return !!(this.dataUrl || this.content.path || this.content.src);
        },

        /**
         * Calculate the progress
         * @returns {number}
         */
        progress() {
            return this.remoteProgress + this.uploadProgress;
        },

        /**
         * Get the uploading status
         * @returns {boolean}
         */
        uploading() {
            return this.progress > 0;
        },

        ivStyles() {
            if (this.imageStyles) {
                return this.imageStyles;
            }
            return {
                width: "100%",
                "max-width": "250px",
                "max-height": "100px",
            };
        },
    },
};
</script>

<style scoped></style>
